import React, { Component } from 'react';
import { isEqual } from 'lodash';
import DateRangeLink from 'containers/buttons/DateRangeLink';
import PropTypes from 'prop-types';
import helpers from 'helpers';
import fastMemoize from 'fast-memoize';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import { insertBeforeItem, insertAfterItem } from 'helpers/utilities';
import { generateFooterData, generateHeaders, applyDefaultColumns } from 'helpers/tables';
import { reducePCVData } from 'helpers/social';
import DefaultTable from 'components/atoms/tables/smarttables/DefaultTable';
import { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';

const DEFAULT_ACCESSORS = ['impressions', 'ecpa_activities', 'ecpa', 'spend'];

const filterOnlyNonDeliveredItems = item => !item.impressions && !item.total_activities;
const createTableData = ({ data, props }) => {
    const dataMapped = data.map(x => ({
        ...x,
        has_delivered: !!(x.impressions || x.total_activities)
    }));
    const { hasROASData, sumPCVWithoutZeroes, sumSpendWithoutZeroes } = reducePCVData({ data: dataMapped });
    const headers = generateHeaders({
        keys: [
            'campaign_activities',
            'clicks',
            'completion_rate',
            'conversions',
            'cost_per_conversion',
            'conversion_rate',
            'conversion_revenue',
            'purchase_conversion_value',
            'purchase_roas',
            'cost_per_lead',
            'cost_per_link_click',
            'cpc',
            'cpcv',
            'ctr',
            'cpconv',
            'ecpa',
            'ecpa_activities',
            'engagements',
            'engagement_rate',
            'impressions',
            'leads',
            'leadgen_grouped',
            'cost_per_leadgen_grouped',
            'link_clicks',
            'link_click_rate',
            'spend',
            'total_activities',
            'reach',
            'has_delivered'
        ],
        colOptions: {
            labelCode: props.entityObjectiveLabelCode
        },
        footerData: generateFooterData({
            data: dataMapped,
            filteredKeys: ['entity_name', 'entity_id'],
            extraData: { sumPCVWithoutZeroes, sumSpendWithoutZeroes }
        })
    });

    let { accessors = DEFAULT_ACCESSORS } = props;
    if (props.showTotalActivities && !accessors.includes('total_activities')) {
        if (accessors.includes('campaign_activities')) {
            accessors = insertBeforeItem(accessors, 'campaign_activities', 'total_activities');
        } else {
            accessors = insertBeforeItem(accessors, 'ecpa_activities', 'total_activities');
        }
    }

    if (props.showeCPA && !accessors.includes('ecpa')) {
        accessors = insertAfterItem(accessors, 'spend', 'ecpa');
    }

    if (props.showSocialCpconv && !accessors.includes('cost_per_conversion')) {
        accessors = insertAfterItem(accessors, 'conversions', 'cost_per_conversion');
    }

    if (props.showConversionRevenue && !accessors.includes('conversion_revenue')) {
        /** Hacky. Since we implemented ROAS in social before conversion revenue everywhere else,
         * we pulled in the concept of "facebook conversion revenue" as "purchase_conversion_value"
         * according to how Facebook labels it. purchase_conversion_value === conversion_revenue,
         * but we use different accessors for it based on whether we are using social data or not.
         * SAD!
         */
        if (props.channel === 'social') {
            accessors = insertAfterItem(accessors, 'conversion_rate', 'purchase_conversion_value');
        } else {
            accessors = insertAfterItem(accessors, 'conversion_rate', 'conversion_revenue');
        }
    }

    if (hasROASData) {
        accessors = insertAfterItem(accessors, 'conversion_rate', 'purchase_roas');
    }
    accessors = insertAfterItem(accessors, 'spend', 'has_delivered');
    // TODO the above logic for inserting columns is getting fragile.
    // We should consider a more sustatainable solution
    const columns = applyDefaultColumns(
        accessors.map(accessor => headers[accessor]),
        ['entity_name', 'impressions', 'conversions', 'conversion_revenue', 'cost_per_conversion', 'purchase_conversion_value', 'spend']
    );
    return { data: dataMapped, columns };
};
const getNonDeliveredCount = data => data.filter(filterOnlyNonDeliveredItems).length;

const memoizedGetNonDeliveredCount = fastMemoize(getNonDeliveredCount);
const memoizedCreatedTableData = fastMemoize(createTableData);

class LayerTwoSummaryTable extends Component {
    constructor(props) {
        super(props);

        this.entityDashboardLink = this.entityDashboardLink.bind(this);
        const showNonDeliveryRows = !!props.showNonDeliveryRows;
        const { data, columns } = memoizedCreatedTableData({
            data: props.data,
            props,
            showNonDeliveryRows
        });

        this.state = {
            data,
            showNonDeliveryRows,
            columns,
            accessors: props.accessors || DEFAULT_ACCESSORS,
            showTotalActivities: props.showTotalActivities,
            showeCPA: props.showeCPA,
            showConversionRevenue: props.showConversionRevenue,
            title: props.title,
            entityId: props.entityId
        };
    }

    entityDashboardLink(row, channel) {
        const channelParam = channel ? `channel/${channel}` : 'summary';
        return (
            <DateRangeLink
                to={`/dashboard/${row.original.entity_id}/${channelParam}`}
                target='_blank'
                title='Go to dashboard'>
                {row.original.entity_name}
            </DateRangeLink>
        );
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(nextProps, this.props)
            || !isEqual(nextState, this.state);
    }

    static getDerivedStateFromProps(props, state) {
        const {
            data: rawData,
            entityId,
            title,
            accessors,
            showTotalActivities,
            showeCPA,
            showConversionRevenue
        } = props;
        const { showNonDeliveryRows } = state;
        const { data, columns } = memoizedCreatedTableData({
            data: rawData,
            props,
            showNonDeliveryRows
        });

        return {
            ...state,
            columns,
            data,
            accessors,
            showTotalActivities,
            showeCPA,
            showConversionRevenue,
            entityId,
            title
        };
    }

    render() {
        const { data = [], columns, title, showNonDeliveryRows } = this.state;
        const { aliasDescriptor, exportName, channel, cacheKey, data: rawData = [] } = this.props;
        const nonDeliveredCount = memoizedGetNonDeliveredCount(rawData);
        const tableColumns = [
            {
                Header: `${aliasDescriptor}`,
                accessor: 'entity_name',
                Cell: cell => <CellDiv value={this.entityDashboardLink(cell.row, channel)} />,
                Footer: (
                    <CellDiv value={'N/A'} type={'NOT_APPLICABLE'} strong />
                ),
                filter: 'textColumnFilter',
                isIndexCol: true
            },
            ...columns
        ];
        const toggle = !!nonDeliveredCount && {
            initialFilter: !showNonDeliveryRows,
            label: 'Hide Undelivered'
        };

        return (
            <ContentTile padding={helpers.contentPadding('smarttable')}>
                <ContentHeader type={'table'} allowExport={false}>{title}</ContentHeader>
                <DefaultTable
                    columns={tableColumns}
                    title={title}
                    data={data}
                    cacheKey={cacheKey}
                    globalSearchPlaceholder='Search'
                    globalSearch={false}
                    exportData
                    exportName={exportName}
                    fileName={exportName}
                    pagination={data.length > RowsPerPageDefault}
                    dataToggle={toggle}
                    aggregate={true}
                    defaultState={{
                        pageSize: RowsPerPageDefault,
                        sortBy: [
                            { id: 'entity_name', desc: false }
                        ]
                    }}
                />
            </ContentTile>
        );
    }
}

LayerTwoSummaryTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        entity_name: PropTypes.string,
        impressions: PropTypes.number,
        total_activities: PropTypes.number,
        ecpa_activities: PropTypes.number,
        ecpa: PropTypes.number,
        spend: PropTypes.number
    })),
    channel: PropTypes.string,
    title: PropTypes.string.isRequired,
    entityObjectiveLabelCode: PropTypes.string,
    aliasDescriptor: PropTypes.string,
    showNonDeliveryRows: PropTypes.bool
};

export default LayerTwoSummaryTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/LayerTwoSummaryTable.js